import { useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import client, { formatApiError } from "@/lib/api";

export default function ForgotPassword() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await client.post(`/auth/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Card className="border-border p-8 shadow-sm">
          <div className="mb-8">
            <h2 className="text-3xl font-bold tracking-tight">{t("forgotPassword.title")}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{t("forgotPassword.subtitle")}</p>
          </div>

          {sent ? (
            <p data-testid="forgot-password-sent" className="text-sm text-foreground">
              {t("forgotPassword.sent")}
            </p>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="data-label">
                  {t("forgotPassword.email")}
                </Label>
                <Input
                  id="email"
                  data-testid="forgot-password-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              {error && (
                <p data-testid="forgot-password-error" className="text-xs font-medium text-destructive">
                  {error}
                </p>
              )}
              <Button data-testid="forgot-password-submit" type="submit" disabled={busy} className="w-full">
                {busy ? t("common.loading") : t("forgotPassword.submit")}
              </Button>
            </form>
          )}

          <Link
            to="/login"
            data-testid="forgot-password-back-link"
            className="mt-6 block text-center text-xs text-muted-foreground hover:text-foreground"
          >
            {t("forgotPassword.backToLogin")}
          </Link>
        </Card>
      </div>
    </div>
  );
}
